import { ReactNode } from "react";
import { useMatch, generatePath, PathParam } from "react-router-dom";
import TeamListPage from "pages/TeamListPage";
import TeamPage from "pages/TeamPage";
import ErrataPage from "pages/ErrataPage";
import AdminPage from "pages/AdminPage";
import AdminQueuePage, {
  AdminQueueTaskPage,
  AdminQueueTaskClaimPage,
} from "pages/AdminQueuePage";
import BigBoardPage from "pages/BigBoardPage";
import ServerHealthPage from "pages/ServerHealthPage";
import WorldPage from "pages/WorldPage";
import HintsDisplay from "components/PuzzlePage/HintsDisplay";
import PuzzleBodyDisplay from "components/PuzzlePage/PuzzleBodyDisplay";
import SolutionBodyDisplay from "components/PuzzlePage/SolutionBodyDisplay";
import { HuntStatsPage, StatsDisplay } from "pages/StatsPage";
import UnlockPage from "pages/UnlockPage";
import VictoryPage from "pages/VictoryPage";
import CertificatePage from "pages/CertificatePage";
import WrapupPage, {
  WrapupShoutoutsPage,
  WrapupAmaPage,
} from "pages/wrapup/WrapupPage";
import WrapupStatsPage from "pages/wrapup/WrapupStatsPage";
import RoundPage from "pages/RoundPage";
import ArchipelagoPage from "pages/ArchipelagoPage";

// ok here's the deal. this file is bad actually because it ends up pulling all
// these dependencies into huntlib. however, I want this setup so we can type
// check our links, and I _don't_ want to have to split it into parallel lists
// in separate locations that have to be kept in sync, so I don't know if this
// is avoidable. I will claim that the issue becomes irrelevant once we're no
// longer hosting django-only pages that only use huntlib. maybe that's copium.

const DjangoRoutes = {
  admin: { path: "/admin" },
  admin_subpage: { path: "/admin/*" },
  impersonate: { path: "/impersonate/:userId" },
  index: { path: "/" },
  about: { path: "/about" },
  password_change: { path: "/password-change" },
  edit_team: { path: "/edit-team" },
  bridge: { path: "/bridge" },
} as const;

export const PuzzleRoutes = {
  piece: {
    path: "/piece/:slug",
    element: <PuzzleBodyDisplay />,
  },
  puzzle: {
    path: "/puzzle/:slug",
    element: <PuzzleBodyDisplay />,
  },
  solution: {
    path: "/solution/:slug",
    element: <SolutionBodyDisplay />,
  },
  hints: {
    path: "/hints/:slug",
    element: <HintsDisplay />,
  },
  stats: {
    path: "/stats/:slug",
    element: <StatsDisplay />,
  },
} as const;

export const WorldRoutes = {
  puzzles: {
    path: "/puzzles",
    element: <WorldPage />,
  },
  round: {
    path: "/round/:slug",
    element: <RoundPage />,
  },
  errata: {
    path: "/errata",
    element: <ErrataPage />,
  },
  unlock: {
    path: "/unlock",
    element: <UnlockPage />,
  },
  teams: {
    path: "/teams",
    element: <TeamListPage />,
  },
  team: {
    path: "/team/:teamName",
    element: <TeamPage />,
  },
  hunt_stats: {
    path: "/stats",
    element: <HuntStatsPage />,
  },
} as const;

export const OuterRoutes = {
  sock_admin: {
    path: "/sock-admin",
    element: <AdminPage />,
  },
  sock_admin_team: {
    path: "/sock-admin/:teamId",
    element: <AdminPage />,
  },
  admin_queue: {
    path: "/admin-queue",
    element: <AdminQueuePage />,
  },
  admin_queue_task: {
    path: "/admin-queue/task/:taskId",
    element: <AdminQueueTaskPage />,
  },
  admin_queue_claim: {
    path: "/admin-queue/task/:taskId/claim",
    element: <AdminQueueTaskClaimPage />,
  },
  bigboard: {
    path: "/bigboard",
    element: <BigBoardPage />,
  },
  health: {
    path: "/health",
    element: <ServerHealthPage />,
  },
  victory: {
    path: "/victory",
    element: <VictoryPage />,
  },
  wrapup: {
    path: "/wrapup",
    element: <WrapupPage />,
  },
  wrapup_stats: {
    path: "/wrapup/stats",
    element: <WrapupStatsPage />,
  },
  wrapup_shoutouts: {
    path: "/wrapup/shoutouts",
    element: <WrapupShoutoutsPage />,
  },
  wrapup_ama: {
    path: "/wrapup/ama",
    element: <WrapupAmaPage />,
  },
  certificate: {
    path: "/cert/:teamId",
    element: <CertificatePage />,
  },
  archipelago: {
    path: "/archipelago",
    element: <ArchipelagoPage />,
  },
} as const;

const _Routes = {
  ...DjangoRoutes,
  ...PuzzleRoutes,
  ...WorldRoutes,
  ...OuterRoutes,
};

// i am so sorry

export type RouteName = keyof typeof _Routes;

export const Routes: {
  [name in RouteName]: {
    path: (typeof _Routes)[name]["path"] & `/${string}`;
    element?: ReactNode;
  };
} = _Routes;

export type RouteSpec<R extends RouteName> = {
  route: R;
} & (PathParam<(typeof _Routes)[R]["path"]> extends never
  ? { params?: never }
  : {
      params: {
        [key in PathParam<(typeof _Routes)[R]["path"]>]: string;
      };
    });

export type DistributiveRouteSpec<R extends RouteName> = R extends RouteName
  ? RouteSpec<R>
  : never;

export function resolveRoute<R extends RouteName>(spec: RouteSpec<R>) {
  return generatePath(Routes[spec.route].path, spec.params);
}

export function useRouteMatch(name: RouteName) {
  return useMatch(_Routes[name]["path"]);
}
